/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { ok } from "./file-system/utils.js";
// Helpers for handling DataCapability objects.
export function isImageURL(nodeValue) {
    if (typeof nodeValue !== "object" || !nodeValue) {
        return false;
    }
    return "image_url" in nodeValue;
}
export function isTextCapabilityPart(part) {
    if (typeof part !== "object" || part === null)
        return false;
    return "text" in part;
}
export function isFileDataCapabilityPart(part) {
    if (typeof part !== "object" || part === null)
        return false;
    return "fileData" in part;
}
export function isFunctionCallCapabilityPart(part) {
    if (typeof part !== "object" || part === null)
        return false;
    return "functionCall" in part;
}
export function isFunctionResponseCapabilityPart(part) {
    if (typeof part !== "object" || part === null)
        return false;
    return "functionResponse" in part;
}
export function isJSONPart(part) {
    if (typeof part !== "object" || part === null)
        return false;
    return "json" in part;
}
export function isListPart(part) {
    if (typeof part !== "object" || part === null)
        return false;
    return "list" in part;
}
export function isLLMContent(nodeValue) {
    if (typeof nodeValue !== "object" || !nodeValue)
        return false;
    if (nodeValue === null || nodeValue === undefined)
        return false;
    return "parts" in nodeValue && Array.isArray(nodeValue.parts);
}
export function isLLMContentArray(nodeValue) {
    if (typeof nodeValue !== "object" || !nodeValue)
        return false;
    if (!Array.isArray(nodeValue))
        return false;
    if (nodeValue === null || nodeValue === undefined)
        return false;
    nodeValue = nodeValue.filter((item) => {
        if (item === null || item === undefined)
            return false;
        return item.role !== "$metadata";
    });
    return (Array.isArray(nodeValue) && nodeValue.every((entry) => isLLMContent(entry)));
}
export function isMetadataEntry(nodeValue) {
    return nodeValue.role === "$metadata";
}
export const isDataCapability = (value) => {
    if (typeof value !== "object" || value === null)
        return false;
    const data = value;
    if (data.kind !== "data")
        return false;
    if ("inlineData" in data)
        return true;
    if ("storedData" in data)
        return true;
    return false;
};
export const asBlob = async (part) => {
    let url;
    if (isStoredData(part)) {
        url = part.storedData.handle;
    }
    else {
        url = `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
    }
    const response = await fetch(url);
    const data = await response.blob();
    return data;
};
export const isStoredData = (value) => {
    if (typeof value !== "object" || value === null)
        return false;
    const data = value;
    if (!("storedData" in data))
        return false;
    if (typeof data.storedData.handle !== "string")
        return false;
    return true;
};
export const isInlineData = (value) => {
    if (typeof value !== "object" || value === null)
        return false;
    const data = value;
    if (!("inlineData" in data))
        return false;
    if (typeof data.inlineData.data !== "string")
        return false;
    return true;
};
export const isSerializedData = (value) => {
    if (typeof value !== "object" || value === null)
        return false;
    const data = value;
    if (!("inlineData" in data))
        return false;
    if (!("handle" in data))
        return false;
    if (typeof data.inlineData.data !== "string")
        return false;
    return true;
};
export async function asBase64(file) {
    if ("Buffer" in globalThis) {
        // Node.js implementation, since Node.js doesn't have FileReader.
        return Buffer.from(await file.arrayBuffer())
            .toString("base64")
            .replace(/=+$/, "");
    }
    else {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result !== "string") {
                    reject("Reader result is not a string");
                    return;
                }
                const [, content] = reader.result.split(",");
                resolve(content.replace(/=+$/, ""));
            };
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(file);
        });
    }
}
export async function retrieveAsBlob(part, graphUrl) {
    if (!isStoredData(part)) {
        throw new Error("Invalid stored data");
    }
    let { handle } = part.storedData;
    if (handle.startsWith(".") && graphUrl) {
        handle = new URL(handle, graphUrl).href;
    }
    const response = await fetch(handle);
    return await response.blob();
}
export async function toInlineDataPart(part, graphUrl) {
    const raw = await retrieveAsBlob(part, graphUrl);
    const mimeType = part.storedData.mimeType;
    const data = await asBase64(raw);
    return { inlineData: { mimeType, data } };
}
export async function toStoredDataPart(part) {
    if (isStoredData(part)) {
        if (part.storedData.handle.startsWith("https://") ||
            part.storedData.handle.startsWith(".")) {
            return part;
        }
    }
    const blob = part instanceof Blob ? part : await asBlob(part);
    const handle = URL.createObjectURL(blob);
    return {
        storedData: {
            handle,
            mimeType: blob.type,
        },
    };
}
export async function transformDataParts(graphUrl, contents, to, transformer) {
    const transformed = [];
    for (const content of contents) {
        const role = content.role || "user";
        const parts = [];
        for (const part of content.parts) {
            let transformedPart = part;
            if ("inlineData" in part) {
                if (to === "ephemeral") {
                    // convert inline to ephemeral
                    const blob = await asBlob(part);
                    transformedPart = transformer.addEphemeralBlob(blob);
                }
                else if (to === "persistent" || to === "persistent-temporary") {
                    const temporary = to === "persistent-temporary";
                    // convert inline to persistent
                    const persisted = await transformer.persistPart(graphUrl, part, temporary);
                    if (!ok(persisted))
                        return persisted;
                    transformedPart = persisted;
                }
            }
            else if ("storedData" in part) {
                const isEphemeral = part.storedData.handle.startsWith("blob:");
                if (to === "ephemeral") {
                    if (!isEphemeral) {
                        // convert persistent to ephemeral
                        const ephemeral = await transformer.persistentToEphemeral(part);
                        if (!ok(ephemeral))
                            return ephemeral;
                        transformedPart = ephemeral;
                    }
                }
                else if (to === "inline") {
                    // convert persisent or ephemeral to inline
                    transformedPart = await toInlineDataPart(part);
                }
                else if (to == "persistent" || to === "persistent-temporary") {
                    if (isEphemeral) {
                        // convert ephemeral to persistent
                        const inline = await toInlineDataPart(part);
                        const temporary = to === "persistent-temporary";
                        const persisted = await transformer.persistPart(graphUrl, inline, temporary);
                        if (!ok(persisted))
                            return persisted;
                        transformedPart = persisted;
                    }
                }
                else if (to === "file") {
                    const file = await transformer.toFileData(graphUrl, part);
                    if (!ok(file))
                        return file;
                    transformedPart = file;
                }
            }
            else if ("fileData" in part) {
                if (to === "file") {
                    const file = await transformer.toFileData(graphUrl, part);
                    if (!ok(file))
                        return file;
                    transformedPart = file;
                }
            }
            parts.push(transformedPart);
        }
        transformed.push({ parts, role });
    }
    return transformed;
}
export function convertStoredPartsToAbsoluteUrls(values, graphUrl) {
    const result = {};
    if (!graphUrl)
        return result;
    const url = parseUrl(graphUrl);
    for (const [key, value] of Object.entries(values || {})) {
        result[key] = convertValue(value);
    }
    return result;
    function convertValue(contents) {
        const converted = [];
        if (!isLLMContentArray(contents))
            return contents;
        for (const content of contents) {
            const role = content.role || "user";
            const parts = [];
            for (const part of content.parts) {
                let convertedPart = part;
                if ("storedData" in part) {
                    if (part.storedData.handle.startsWith(".")) {
                        convertedPart = {
                            storedData: {
                                handle: new URL(part.storedData.handle, url).href,
                                mimeType: part.storedData.mimeType,
                            },
                        };
                    }
                }
                parts.push(convertedPart);
            }
            converted.push({ parts, role });
        }
        return converted;
    }
}
function parseUrl(s) {
    try {
        return new URL(s);
    }
    catch (e) {
        return;
    }
}
//# sourceMappingURL=common.js.map